import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { ChevronLeft, ChevronRight, Calendar, Clock, User, ArrowRight } from 'lucide-react';
import AppHeader from './AppHeader';
import InfoModal from './InfoModal';
import type { ScheduleTherapist } from '../types/schedule';
import { subscribePublicTherapists, toScheduleTherapists } from '../services/publicTherapistsService';
import {
  type BusySlot,
  type TimeSlot,
  SLOTS_CACHE_TTL_MS,
  computeAvailableSlots,
  computePreviewSlots,
  formatDateKey,
  getUpcomingWorkingDates,
  readSlotsCache,
  writeSlotsCache,
} from '../utils/scheduleSlots';

const Schedule: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [therapists, setTherapists] = useState<ScheduleTherapist[]>([]);
  const [selectedTherapist, setSelectedTherapist] = useState<ScheduleTherapist | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTherapists, setIsLoadingTherapists] = useState(true);
  const [isRefreshingSlots, setIsRefreshingSlots] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  /** Cache em memória (complementa localStorage). */
  const slotsCacheRef = useRef<Map<string, { fetchedAt: number; busySlots: BusySlot[] }>>(new Map());
  const prefetchInFlightRef = useRef<Set<string>>(new Set());

  const planId = new URLSearchParams(location.search).get('plan');
  const therapistId = new URLSearchParams(location.search).get('therapistId');

  const fetchTherapistById = (id: string) => therapists.find((t) => t.id === id) || null;

  const cacheKey = useCallback(
    (therapist: ScheduleTherapist, date: Date) => `${therapist.id}:${formatDateKey(date)}`,
    []
  );

  const loadBusySlots = useCallback(
    async (therapist: ScheduleTherapist, date: Date, options?: { silent?: boolean }) => {
      const key = cacheKey(therapist, date);
      const dateKey = formatDateKey(date);

      if (!options?.silent) {
        setIsRefreshingSlots(true);
        setError(null);
      }

      try {
        const getSlotsFunction = httpsCallable(functions, 'getAvailableSlots');
        const result = await getSlotsFunction({
          therapistId: therapist.id,
          date: date.toISOString(),
        });
        const { busySlots } = (result.data as { busySlots: BusySlot[] }) || { busySlots: [] };
        const normalized = busySlots || [];

        const entry = { fetchedAt: Date.now(), busySlots: normalized };
        slotsCacheRef.current.set(key, entry);
        writeSlotsCache(therapist.id, dateKey, normalized);

        if (selectedTherapist?.id === therapist.id && selectedDate && formatDateKey(selectedDate) === dateKey) {
          setAvailableSlots(computeAvailableSlots(therapist, date, normalized));
        }
        return normalized;
      } catch (err) {
        if (!options?.silent) {
          console.error('Erro ao buscar horários:', err);
          setError('Não foi possível carregar os horários. O terapeuta pode não ter conectado a agenda.');
        }
        return null;
      } finally {
        if (!options?.silent) setIsRefreshingSlots(false);
      }
    },
    [cacheKey, selectedDate, selectedTherapist?.id]
  );

  const prefetchSlots = useCallback(
    async (therapist: ScheduleTherapist, date: Date) => {
      const key = cacheKey(therapist, date);
      const mem = slotsCacheRef.current.get(key);
      const ls = readSlotsCache(therapist.id, formatDateKey(date));
      const fresh = (entry?: { fetchedAt: number } | null) =>
        !!entry && Date.now() - entry.fetchedAt < SLOTS_CACHE_TTL_MS;

      if (fresh(mem) || fresh(ls)) return;
      if (prefetchInFlightRef.current.has(key)) return;

      prefetchInFlightRef.current.add(key);
      try {
        await loadBusySlots(therapist, date, { silent: true });
      } finally {
        prefetchInFlightRef.current.delete(key);
      }
    },
    [cacheKey, loadBusySlots]
  );

  // Buscar terapeutas disponíveis (JSON estático + Firestore — sem cold start de Cloud Function)
  useEffect(() => {
    const applyTherapistList = (therapistList: ScheduleTherapist[]) => {
      setTherapists(therapistList);

      let _selectedTherapist = null;
      if (therapistId) {
        _selectedTherapist = therapistList.find((t) => t.id === therapistId) || null;
        if (_selectedTherapist) setSelectedTherapist(_selectedTherapist);
      }

      if (therapistList.length > 0 && !_selectedTherapist) {
        setSelectedTherapist(therapistList[0]);
      }
    };

    setIsLoadingTherapists(true);
    setError(null);

    const unsubscribe = subscribePublicTherapists(
      (list) => {
        applyTherapistList(toScheduleTherapists(list));
        setIsLoadingTherapists(false);
        if (list.length === 0) {
          setError('Não foi possível carregar os terapeutas.');
        } else {
          setError(null);
        }
      },
      (err) => {
        setIsLoadingTherapists(false);
        setError('Não foi possível carregar os terapeutas.');
        console.error('Erro ao carregar terapeutas:', err);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [therapistId]);

  // Limpa cache em memória ao trocar terapeuta
  useEffect(() => {
    slotsCacheRef.current.clear();
  }, [selectedTherapist?.id]);

  // Prefetch dos próximos dias com jornada (em background)
  useEffect(() => {
    if (!selectedTherapist) return;
    const dates = getUpcomingWorkingDates(selectedTherapist, new Date(), 5);
    dates.forEach((d) => void prefetchSlots(selectedTherapist, d));
  }, [selectedTherapist, prefetchSlots]);

  // Horários: exibição imediata (preview) + cache stale-while-revalidate + refresh
  useEffect(() => {
    if (!selectedTherapist || !selectedDate) {
      setAvailableSlots([]);
      return;
    }

    let cancelled = false;
    const key = cacheKey(selectedTherapist, selectedDate);
    const dateKey = formatDateKey(selectedDate);

    // 1) Mostra horários da jornada imediatamente (sem esperar rede)
    setAvailableSlots(computePreviewSlots(selectedTherapist, selectedDate));
    setSelectedSlot(null);
    setError(null);

    const memCached = slotsCacheRef.current.get(key);
    const lsCached = readSlotsCache(selectedTherapist.id, dateKey);
    const cached = memCached || lsCached;
    const isFresh = cached && Date.now() - cached.fetchedAt < SLOTS_CACHE_TTL_MS;

    // 2) Cache hit: aplica ocupação na hora
    if (cached) {
      if (!memCached && lsCached) slotsCacheRef.current.set(key, lsCached);
      setAvailableSlots(computeAvailableSlots(selectedTherapist, selectedDate, cached.busySlots));
      if (!isFresh) {
        void loadBusySlots(selectedTherapist, selectedDate, { silent: true });
      }
      return () => { cancelled = true; };
    }

    // 3) Cache miss: busca em background (preview já visível)
    void (async () => {
      setIsRefreshingSlots(true);
      const busy = await loadBusySlots(selectedTherapist, selectedDate);
      if (!cancelled && busy) {
        setAvailableSlots(computeAvailableSlots(selectedTherapist, selectedDate, busy));
      }
    })();

    return () => { cancelled = true; };
  }, [selectedTherapist, selectedDate, cacheKey, loadBusySlots]);

  const handlePayment = async () => {
    setError(null);

    if (!user) {
      setError('Erro: Utilizador não está autenticado. Por favor, faça login novamente.');
      return;
    }
    if (!selectedTherapist) {
      setError('Erro: Nenhum terapeuta foi selecionado. Por favor, escolha um terapeuta.');
      return;
    }
    if (!selectedDate) {
      setError('Erro: Nenhuma data foi selecionada. Por favor, escolha uma data.');
      return;
    }
    if (!selectedSlot) {
      setError('Erro: Nenhum horário foi selecionado. Por favor, escolha um horário.');
      return;
    }
    if (!planId) {
      setError('Erro: Plano não identificado. Por favor, volte à seleção de planos.');
      return;
    }

    setIsLoading(true);

    try {
      const appointmentDateTime = new Date(selectedDate);
      const [hours, minutes] = selectedSlot.split(':');
      appointmentDateTime.setHours(Number(hours), Number(minutes), 0, 0);

      if (appointmentDateTime < new Date()) {
        throw new Error('A data/hora selecionada já passou. Por favor, escolha um horário futuro.');
      }

      const sessionDuration = selectedTherapist.sessionDuration || 60;
      if (!functions) throw new Error('Serviço de agendamento não está disponível. Tente recarregar a página.');

      const createAppointment = httpsCallable(functions, 'createConstellationAppointment');
      let result: { data: unknown };
      try {
        result = await createAppointment({
          therapistId: selectedTherapist.id,
          appointmentDateTime: appointmentDateTime.toISOString(),
          sessionDuration,
        });
      } catch (functionError: unknown) {
        const fe = functionError as { code?: string; message?: string };
        if (fe?.code === 'functions/unauthenticated') throw new Error('Erro de autenticação. Por favor, faça login novamente.');
        if (fe?.code === 'functions/invalid-argument') throw new Error(`Dados inválidos: ${fe?.message}`);
        if (fe?.code === 'functions/failed-precondition') {
          sessionStorage.setItem(
            'pendingConstellationAppointment',
            JSON.stringify({
              therapistId: selectedTherapist.id,
              appointmentDateTime: appointmentDateTime.toISOString(),
              planId,
            })
          );
          navigate('/planos?secao=participante&intencao=constelacao');
          return;
        }
        if (fe?.code === 'functions/internal') throw new Error('Erro interno do servidor. Tente novamente.');
        if (fe?.code === 'functions/unavailable') throw new Error('Serviço temporariamente indisponível. Tente novamente em alguns minutos.');
        throw new Error(`Erro no agendamento: ${fe?.message || 'Erro desconhecido'}`);
      }

      const responseData = result.data as { success?: boolean; appointmentId?: string; message?: string };
      if (!responseData || responseData.success === false) {
        throw new Error(responseData?.message || 'Erro ao criar agendamento');
      }
      if (responseData.success !== true) throw new Error('Resposta inesperada do servidor.');

      setShowSuccessModal(true);
    } catch (err: unknown) {
      const e = err as { message?: string; code?: string };
      setError(e?.message || 'Não foi possível criar o agendamento.');
    } finally {
      setIsLoading(false);
    }
  };

  const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const totalDays = daysInMonth(year, month);
    const firstDay = firstDayOfMonth(year, month);
    const calendarDays = [];

    for (let i = 0; i < firstDay; i++) {
      calendarDays.push(<div key={`empty-${i}`} className="p-2" />);
    }

    for (let day = 1; day <= totalDays; day++) {
      const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
      const isSelected = selectedDate?.toDateString() === new Date(year, month, day).toDateString();
      const currentDay = new Date(year, month, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const isNotInPast = currentDay >= today;
      const dayOfWeek = currentDay.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const workingHours = selectedTherapist?.horariosDisponiveis?.[dayOfWeek];
      const hasWorkingHours = workingHours && workingHours.length > 0;
      const isAvailable = selectedTherapist && isNotInPast && hasWorkingHours;

      calendarDays.push(
        <button
          key={day}
          disabled={!isAvailable}
          onMouseEnter={() => {
            if (isAvailable && selectedTherapist) void prefetchSlots(selectedTherapist, currentDay);
          }}
          onClick={() => isAvailable && setSelectedDate(new Date(year, month, day))}
          className={`p-2 text-center rounded-full transition-colors w-10 h-10
            ${!isAvailable ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-teal-100'}
            ${isToday ? 'font-bold text-teal-600' : ''}
            ${isSelected ? 'bg-teal-600 text-white' : ''}
          `}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="grid grid-cols-7 gap-2 place-items-center">
        {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
          <div key={i} className="font-bold text-gray-500">{d}</div>
        ))}
        {calendarDays}
      </div>
    );
  };

  const showSlotGrid = availableSlots.length > 0 || isRefreshingSlots;

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <AppHeader />
      <div className="container mx-auto px-4 max-w-4xl mt-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Agende sua Sessão</h1>
        <div className="bg-white p-8 rounded-2xl shadow-lg space-y-8">
          <div>
            <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2"><User />Selecione o Terapeuta</h2>
            {therapists.length > 0 ? (
              <select
                value={selectedTherapist?.id || ''}
                onChange={(e) => setSelectedTherapist(fetchTherapistById(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-lg"
              >
                {therapists.map((t) => (
                  <option key={t.id} value={t.id}>{t.nomePublico} {t.title ? '-' : ''} {t.title}</option>
                ))}
              </select>
            ) : isLoadingTherapists ? (
              <p className="text-gray-500">Carregando terapeutas...</p>
            ) : (
              <p className="text-gray-500">Nenhum terapeuta disponível para agendamento.</p>
            )}
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2"><Calendar />Escolha a Data</h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}><ChevronLeft /></button>
                <span className="font-bold text-lg">{currentDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}</span>
                <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))}><ChevronRight /></button>
              </div>
              {renderCalendar()}
            </div>
          </div>

          {selectedDate && (
            <div>
              <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Clock />Escolha o Horário
                {isRefreshingSlots && (
                  <span className="text-xs font-normal text-gray-400 animate-pulse">atualizando...</span>
                )}
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                {showSlotGrid ? (
                  availableSlots.map((slot) => (
                    <button
                      key={slot.time}
                      onClick={() => setSelectedSlot(slot.time)}
                      className={`p-3 border rounded-lg transition-colors ${
                        selectedSlot === slot.time
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'border-gray-300 hover:bg-teal-100'
                      } ${isRefreshingSlots ? 'opacity-90' : ''}`}
                    >
                      {slot.time}
                    </button>
                  ))
                ) : (
                  <p className="col-span-full text-center text-gray-500">Nenhum horário disponível para esta data.</p>
                )}
              </div>
            </div>
          )}

          {selectedSlot && (
            <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-lg mt-8">
              <h3 className="text-xl font-bold text-teal-800 mb-4">Resumo do Agendamento</h3>
              <div className="space-y-2">
                <p><strong>Terapeuta:</strong> {selectedTherapist?.nomePublico}</p>
                <p><strong>Data:</strong> {selectedDate?.toLocaleDateString('pt-BR')}</p>
                <p><strong>Horário:</strong> {selectedSlot}</p>
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <button
                onClick={handlePayment}
                disabled={isLoading || isRefreshingSlots}
                className="w-full mt-6 bg-teal-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-teal-700 disabled:bg-teal-400 transition flex items-center justify-center gap-2"
              >
                {isLoading
                  ? 'Processando...'
                  : isRefreshingSlots
                    ? 'Verificando horário...'
                    : planId?.includes('constelar')
                      ? 'Confirmar horário e pagar'
                      : 'Confirmar'}{' '}
                <ArrowRight />
              </button>
            </div>
          )}
        </div>
      </div>

      <InfoModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/painel');
        }}
        title="Agendamento Confirmado!"
        message="Sua sessão de constelação foi agendada com sucesso. Você pode visualizar os detalhes no seu painel."
        variant="success"
        buttonText="Ir para o Painel"
      />
    </div>
  );
};

export default Schedule;
